import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useFetchSensorDataMetaDataQuery, useFetchSensorDataMutation } from '@/store/api/dataLayersApi';
import {
  transformOperatorProficiencyMetadata,
  extractFilterValues,
  type OperatorProficiencyMetadata,
} from '@/modules/AssetsPreviewPage/OperatorProficiency/utils/transformMetadata';

export const useOperatorProficiencyData = () => {
  const [operatorData, setOperatorData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any>(null);
  const [listData, setListData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transformedMetadata, setTransformedMetadata] = useState<OperatorProficiencyMetadata | null>(null);
  const [rawMetadataLayers, setRawMetadataLayers] = useState<any[]>([]);

  // Filter state - separate primitive values to avoid object reference issues
  const [monthsRange, setMonthsRange] = useState<number | undefined>(undefined);
  const [assetTypeList, setAssetTypeList] = useState<string[]>([]);

  // Track if filters have been initialized
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Track last fetch to prevent duplicates
  const lastFetchKeyRef = useRef<string>('');
  const isFetchingRef = useRef(false);

  // Refs for latest values (to avoid stale closures)
  const dataLayersRef = useRef<any[]>([]);
  const monthsRangeRef = useRef<number | undefined>(undefined);
  const assetTypeListRef = useRef<string[]>([]);

  // Keep refs updated
  dataLayersRef.current = rawMetadataLayers;
  monthsRangeRef.current = monthsRange;
  assetTypeListRef.current = assetTypeList;

  // Stable query params for metadata (doesn't depend on filters)
  const metadataQueryParams = useMemo(() => ({
    site_id: 'all',
    asset_id: 'fleet',
    asset_category: 'mobile',
    component_id: null,
    from_date: null,
    to_date: null,
    asset_name: 'Fleet Overview',
    asset_supplier: null,
    asset_model: null,
    data_layer: 'operator_proficiency',
    full_pull: false,
    months_range: undefined,
    asset_type: [],
  }), []);

  // Fetch metadata
  const {
    data: rawMetadata,
    isLoading: metadataLoading,
    error: metadataError,
  } = useFetchSensorDataMetaDataQuery(metadataQueryParams);

  // Mutation for fetching actual data
  const [fetchSensorData] = useFetchSensorDataMutation();

  // Transform metadata when it changes
  useEffect(() => {
    if (!rawMetadata || filtersInitialized) return;

    const transformed = transformOperatorProficiencyMetadata(rawMetadata);
    setTransformedMetadata(transformed);

    // Store raw data layers for graph definitions
    const dataLayers = rawMetadata.tabs?.[0]?.dataLayers || [];
    setRawMetadataLayers(dataLayers);

    // Update filter state from metadata defaults
    const filterValues = extractFilterValues(transformed);

    // Get all asset types from metadata
    const assetTypeFilter = transformed.filters.find(f => f.id === 'asset_type');
    const allAssetTypes = assetTypeFilter?.options
      ?.filter(opt => opt.value !== 'all')
      ?.map(opt => String(opt.value)) || [];

    // Get months_range default from metadata - respect the 'checked' property from API
    const monthsRangeFilter = transformed.filters.find(f => f.id === 'months_range');
    // First try extractFilterValues (reads checked option), then find checked option directly
    const checkedMonthsOption = monthsRangeFilter?.options.find(opt => opt.checked);
    const rawDefaultMonthsRange = filterValues.months_range ?? checkedMonthsOption?.value ?? monthsRangeFilter?.options[0]?.value ?? 1;
    // Ensure it's always a number, not a string
    const defaultMonthsRange = typeof rawDefaultMonthsRange === 'string'
      ? parseInt(rawDefaultMonthsRange, 10)
      : typeof rawDefaultMonthsRange === 'number'
      ? rawDefaultMonthsRange
      : 1;

    // Set filters initialized FIRST to prevent re-running
    setFiltersInitialized(true);

    // Then set filter values - defaultMonthsRange is guaranteed to be a number now
    setMonthsRange(defaultMonthsRange);
    setAssetTypeList(allAssetTypes.length > 0 ? allAssetTypes : []);

    // If no data layers available, stop loading immediately
    if (dataLayers.length === 0) {
      setLoading(false);
    }
  }, [rawMetadata, filtersInitialized]);

  // Handle case where metadata loaded but we can't fetch data (missing required params)
  // Also add timeout fallback to prevent infinite loading state
  useEffect(() => {
    if (!filtersInitialized || metadataLoading) return;

    // Check if we CAN'T fetch - set loading to false immediately
    const cannotFetch =
      rawMetadataLayers.length === 0 || // No data layers
      assetTypeList.length === 0 || // No asset types
      !monthsRange; // No months range

    if (cannotFetch) {
      setLoading(false);
      return;
    }

    // If we CAN fetch, add a timeout fallback in case fetch hangs
    // This prevents infinite loading states due to network issues
    const timeoutId = setTimeout(() => {
      setLoading((currentLoading) => {
        if (currentLoading) {
          return false;
        }
        return currentLoading;
      });
    }, 15000);

    return () => clearTimeout(timeoutId);
  }, [filtersInitialized, rawMetadataLayers.length, assetTypeList.length, monthsRange, metadataLoading]);

  // Handle metadata error (e.g., 404) - stop loading and show empty state
  useEffect(() => {
    if (metadataError && !metadataLoading) {
      setLoading(false);
    }
  }, [metadataError, metadataLoading]);

  // Create a stable fetch key to prevent duplicate requests
  // Only create key when we have all required data (including asset types)
  const fetchKey = useMemo(() => {
    if (!filtersInitialized || rawMetadataLayers.length === 0 || assetTypeList.length === 0 || !monthsRange) {
      return '';
    }
    return `${monthsRange}-${assetTypeList.join(',')}-${rawMetadataLayers.length}`;
  }, [monthsRange, assetTypeList, filtersInitialized, rawMetadataLayers.length]);

  // Fetch data when filters change
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      // Skip if not ready or already fetched with same params
      if (!fetchKey) {
        // Don't set loading to false - we're waiting for filters to initialize
        return;
      }

      if (fetchKey === lastFetchKeyRef.current) {
        setLoading(false);
        return;
      }

      // Prevent concurrent fetches
      if (isFetchingRef.current) {
        return;
      }
      isFetchingRef.current = true;

      const layers = dataLayersRef.current;
      const months = monthsRangeRef.current;
      const assets = assetTypeListRef.current;

      if (layers.length === 0 || !months || assets.length === 0) {
        isFetchingRef.current = false;
        setLoading(false);
        return;
      }

      lastFetchKeyRef.current = fetchKey;

      try {
        setLoading(true);

        // Build query params for each request
        const requestParams = {
          ...metadataQueryParams,
          months_range: months,
          asset_type: assets,
        };

        // Fetch data for each data layer - body.id tells API which specific layer to fetch
        const fetchPromises = layers.map(async (dataLayer: any) => {
          const result = await fetchSensorData({
            params: requestParams,
            body: dataLayer,
          }).unwrap();

          return { layerId: dataLayer.id, result };
        });

        const results = await Promise.all(fetchPromises);

        // Only update state if component is still mounted
        if (isMounted) {
          results.forEach((item) => {
            if (!item) return;

            const { layerId, result } = item;

            // Handle different response structures
            if (result?.tabs?.[0]?.dataLayers) {
              // Try to get the first dataLayer from response (BE now returns matching layer directly)
              const responseLayer = result.tabs[0].dataLayers[0];
              // Use the response's ID if available, fallback to request layerId
              const responseId = responseLayer?.id || layerId;
              const data = responseLayer?.graphs?.[0]?.data;

              // Match using the response ID
              if (responseId === 'operator_data' || responseId === 'drivers_performance') {
                setOperatorData(data || []);
              } else if (responseId === 'alarms_per_month_average' || responseId === 'fleet_abuse_count') {
                setChartData(data || null);
              } else if (responseId === 'alarm_type_avg' || responseId === 'fleet_frequent_abuses') {
                setListData(data || null);
              }
            } else if (result?.data) {
              // Response format: { id: "xxx", data: [...] }
              const responseId = result.id || layerId;

              // Match using response ID
              if (responseId === 'operator_data' || responseId === 'drivers_performance') {
                setOperatorData(result.data);
              } else if (responseId === 'alarms_per_month_average' || responseId === 'fleet_abuse_count') {
                setChartData(result.data);
              } else if (responseId === 'alarm_type_avg' || responseId === 'fleet_frequent_abuses') {
                setListData(result.data);
              }
            }
          });
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch data');
        }
      } finally {
        isFetchingRef.current = false;
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [fetchKey]);

  // Update filter function
  const updateFilter = useCallback((filterId: string, value: any) => {
    if (filterId === 'months_range') {
      setMonthsRange(value);
    } else if (filterId === 'asset_type') {
      setAssetTypeList(value);
    }
  }, []);

  // Compose filters object for external consumers
  const filters = useMemo(() => ({
    months_range: monthsRange,
    asset_type: assetTypeList,
  }), [monthsRange, assetTypeList]);

  return {
    data: operatorData,
    chartData,
    listData,
    dataLayers: rawMetadataLayers,
    loading,
    error: error || metadataError,
    metadata: transformedMetadata,
    filters,
    updateFilter,
  };
};
